<template>
  <div class="grid-container">
    <div class="grid-x legend-row">
      <div class="small-10 cell">
        <h2 class="title">
          Attracting Top 10% of Outperformers
        </h2>
        <h3 class="title">
          Candidates with a Performance Index in the top 10%
        </h3>
      </div>
      <div class="small-2 cell text-right">
        <rare-chart-rank
          :total="baseTotal"
          :rank="baseRank"
          :display="displayRank"
        />
      </div>
    </div>

    <div v-if="displayMode === 'double' || displayMode === 'single'">
      <div class="grid-x graph-row">
        <div
          v-if="displayMode === 'single'"
          class="small-1 cell"
        >
&nbsp;
        </div>
        <div
          class="cell"
          style="margin-left: -20px;"
          :class="{ 'small-10': displayMode === 'single', 'small-6': displayMode === 'double' } "
        >
          <rare-industry-overview-base-chart
            :name-in-labels="nameInLabels"
            :chart-data="performanceIndexOverviewData.base"
            :labels="performanceIndexOverviewLabels.base"
            :color="performanceIndexOverviewColors.base"
            :legend-text="pILegendDataThis"
            :canvas-i-d="'base'"
            :page="'performance'"
            :display-mode="displayMode"
            :max-decimal="pIMaxDecimal"
            :max-percentage="pIMaxPercentage"
            :force-rank-offset="true"
            :total="calculateTotal(performanceIndexOverviewData.base,
                                   performanceIndexOverviewLabels.base)"
            :rank="calculateRank(performanceIndexOverviewLabels.base)"
          />
        </div>
        <div
          v-if="displayMode === 'double'"
          class="small-6 cell divider"
        >
          <rare-industry-overview-prev-chart
            :name-in-labels="nameInLabels"
            :chart-data="performanceIndexOverviewData.prevYear1"
            :labels="performanceIndexOverviewLabels.prevYear1"
            :color="performanceIndexOverviewColors.prevYear1"
            :legend-text="pILegendDataPrev"
            :canvas-i-d="'prevYear'"
            :page="'performance'"
            :display-mode="displayMode"
            :max-decimal="pIMaxDecimal"
            :max-percentage="pIMaxPercentage"
            :force-rank-offset="true"
            :total="calculateTotal(performanceIndexOverviewData.prevYear1,
                                   performanceIndexOverviewLabels.prevYear1)"
            :rank="calculateRank(performanceIndexOverviewLabels.prevYear1)"
          />
        </div>
        <div
          v-if="displayMode === 'single'"
          class="small-1 cell"
        >
&nbsp;
        </div>
      </div>
    </div>

    <div v-else-if="displayMode === 'table'">
      <rare-industry-3YOY-table
        :name-in-labels="nameInLabels"
        :client-names="performanceIndexOverviewTable.clientNameArray"
        :base-data="performanceIndexOverviewTable.baseDataArray"
        :prev-year1data="performanceIndexOverviewTable.prevYear1Array"
        :prev-year2data="performanceIndexOverviewTable.prevYear2Array"
        :prev-year3data="performanceIndexOverviewTable.prevYear3Array"
      />
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex';

import RareIndustryOverviewChart from '../../components/charts/RareIndustryOverviewChart.vue';
import RareIndustryOverview3YOYTable from '../../components/charts/RareIndustryOverview3YOYTable.vue';
import RareChartRank from '../../components/controls/RareChartRank.vue';

export default {
  name: 'PerformanceIndex',
  components: {
    'rare-industry-overview-base-chart': RareIndustryOverviewChart,
    'rare-industry-overview-prev-chart': RareIndustryOverviewChart,
    'rare-industry-3YOY-table': RareIndustryOverview3YOYTable,
    'rare-chart-rank': RareChartRank,
  },
  data() {
    return {
      nameInLabels: '',
      initialLoad: true,
      chartData: {},
      performanceIndexOverviewData: {
        base: [0, 0, 0],
        prevYear1: [0, 0, 0],
        prevYear2: [0, 0, 0],
        prevYear3: [0, 0, 0],
      },
      performanceIndexOverviewLabels: {
        base: ['', '', ''],
        prevYear1: ['', '', ''],
        prevYear2: ['', '', ''],
        prevYear3: ['', '', ''],
      },
      performanceIndexOverviewColors: {
        base: 'blue',
        prevYear1: 'green',
        prevYear2: 'green',
        prevYear3: 'green',
      },
      performanceIndexOverviewTable: [],
    };
  },
  computed: {
    ...mapGetters({
      rank: '$_performance/pIRank',
      total: '$_performance/pITotal',
      displayMode: '$_performance/pIDisplayMode',
      getPIData: '$_performance/getPIData',
      pILegendData: '$_performance/pILegendData',
      pIMaxPercentage: '$_performance/pIPageMaxPercent',
      pIMaxDecimal: '$_performance/pIPageMaxDecimal',
      tableSortColumn: 'tableSortColumn',
      currentClientDisplayName: 'currentClientDisplayName',
      currentClient: 'currentClientDisplayName',
      comparisonMode: 'comparisonMode',
      comparisonYears: 'comparisonYears',
      dataFormat: 'dataFormat',
      singleClientMode: 'singleClientMode',
      rareMode: 'rareMode',
      totalApplications: 'totalApplicationsBase',
    }),
    displayRank() {
      return this.singleClientMode && ['single', 'table'].includes(this.displayMode);
    },
    pILegendDataThis() {
      return `This year ${this.pILegendData[0]}`;
    },
    pILegendDataPrev() {
      return `Last year ${this.pILegendData[1]}`;
    },
    baseTotal() {
      return this.calculateTotal(
        this.performanceIndexOverviewData.base,
        this.performanceIndexOverviewLabels.base,
      );
    },
    baseRank() {
      return this.calculateRank(this.performanceIndexOverviewLabels.base);
    },
  },
  created() {
    this.$store.commit('updatePageTitle', this.$store.getters.currentClientDisplayName);
    this.$store.commit('updateShowLogoutIcon', true);
    this.$store.commit('updateShowDownloadIcon', true);
    this.$store.commit('updateShowNavigationLinks', true);
    this.$store.commit('updateShowControlPanel', true);
  },
  mounted() {
    this.track();
    document.title = `CRS Dashboard | ${this.$route.name}`;
    this.updateCharts();

    this.$root.$on('updateClient', () => {
      this.clientChange();
    });
    this.$root.$on('updateCharts', () => {
      this.updateCharts();
    });
    this.$root.$on('renderCharts', () => {
      this.renderCharts();
    });
  },
  beforeDestroy() {
    this.$store.commit('updateInitialLoad', { page: 'industry', value: false });
    this.$store.commit(
      'updateCurrentParamsHash',
      'industryOverviewParamsExitHash',
    );
    this.$root.$off('updateClient');
    this.$root.$off('updateCharts');
    this.$root.$off('renderCharts');
  },
  methods: {
    track() {
      this.$gtag.pageview({
        page_path: `/performanceindex/${this.currentClient === '' ? 'rare' : this.currentClient}`,
      });
    },
    clientChange() {
      this.$store.commit('updatePageTitle', this.$store.getters.currentClientDisplayName);
      this.updateCharts();
    },
    calculateTotal(data, labels) {
      const total = parseInt(data.length, 10);
      const industryAverageOffset = labels.indexOf('industry_average') === -1 ? 0 : 1;
      return total - industryAverageOffset;
    },
    findNameInLabels(labels) {
      // We should be able to find the rank from searching for the display name in the labels array.
      // But sometimes the label contains the user name. To complicate things further sometimes the
      // Labels array contains neither the display or user name. In that case we resort to getting
      // The client name form the request params object and search for the username using that.
      const displayName = this.$store.getters.getDisplayNameFromUsername(this.currentClient);
      const userName = this.$store.getters.getUsernameFromDisplayName(this.currentClient);
      const displayNameWrong = labels.indexOf(displayName) === -1;
      const userNameWrong = labels.indexOf(userName) === -1;

      let nameInLabels;

      if (displayNameWrong && userNameWrong) {
        const requestParamsObj = this.$store.getters.requestParams;
        const requestParamsName = requestParamsObj.currentClient;
        nameInLabels = this.$store.getters.getUsernameFromDisplayName(requestParamsName);
        this.nameInLabels = nameInLabels;
      } else {
        nameInLabels = (displayNameWrong ? userName : displayName);
        this.nameInLabels = nameInLabels;
      }

      return nameInLabels;
    },
    calculateRank(labels) {
      const clientPos = labels.indexOf(this.findNameInLabels(labels));
      const industryAvPos = labels.indexOf('industry_average');
      const offset = ((this.dataFormat === 'decimal') || (clientPos < industryAvPos) || (industryAvPos === -1)) ? 1 : 0;
      return clientPos + offset;
    },
    updateCharts() {
      /* TO DO: stop this component commiting mutation directly unless sync is required */
      this.$store.commit('updateIndustryLoadingState', 'loading');
      this.$store.dispatch('$_performance/updateSummaryCharts').then(() => {
        this.renderCharts();
        if (this.comparisonMode === 'industry') {
          this.$store.commit('$_performance/updatePIDisplayMode', 'single');
        } else if (this.comparisonMode === 'date') {
          if (this.comparisonYears === '1') {
            this.$store.commit('$_performance/updatePIDisplayMode', 'double');
          } else if (this.comparisonYears === '3') {
            this.$store.commit('$_performance/updatePIDisplayMode', 'table');
          }
        }
        this.$store.commit('updateIndustryLoadingState', 'stopped');
      }, (error) => { // eslint-disable-next-line
        alert(`Looks like a connection error connecting to our database. Please refresh the page and try again. - ${error}`);
      });
    },
    renderCharts() {
      this.performanceIndexOverviewData.base = this.getPIData(
        {
          comparison: 'base',
          type: 'data',
          dataFormat: this.dataFormat,
          rareMode: this.rareMode,
        },
      );
      this.performanceIndexOverviewLabels.base = this.getPIData(
        {
          comparison: 'base',
          type: 'labels',
          dataFormat: this.dataFormat,
          rareMode: this.rareMode,
        },
      );

      if (this.$store.getters.comparisonMode === 'date'
          && this.comparisonYears === '1') {
        this.performanceIndexOverviewData.prevYear1 = this.getPIData(
          {
            comparison: 'prevYear1',
            type: 'data',
            dataFormat: this.dataFormat,
            rareMode: this.rareMode,
          },
        );
        this.performanceIndexOverviewLabels.prevYear1 = this.getPIData(
          {
            comparison: 'prevYear1',
            type: 'labels',
            dataFormat: this.dataFormat,
            rareMode: this.rareMode,
          },
        );
      } else {
        this.performanceIndexOverviewData.prevYear1 = new Array(
          this.performanceIndexOverviewData.base.length,
        ).fill(0);
        this.performanceIndexOverviewLabels.prevYear1 = new Array(
          this.performanceIndexOverviewLabels.base.length,
        ).fill('');
      }
      if (this.$store.getters.comparisonMode === 'date'
          && this.comparisonYears === '3') {
        this.performanceIndexOverviewTable = this.getPITableData(
          { dataFormat: this.dataFormat },
        );
      }
      return true;
    },
  },
};
</script>

<style lang="scss" scoped>

  @import "~@/css/global.scss";

  h2 {
    color: #323C44;
    font-size: 24px;
    font-weight: bold;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: 4px;
    line-height: 1;
    margin: 0;
    text-transform: uppercase;
  }

  h3 {
    color: #4a5b68;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 1px;
    line-height: 1.43;
    text-transform: none;
  }

  .legend-row {
    margin: 35px 0 0 0;
  }

  .graph-row {
    margin-top: 35px;
  }

  .topSpacer {
    margin-top: 25px;
    margin-bottom: 40px;
    padding-bottom: 25px;
    border-bottom: solid 1px $common-mid-grey;
  }

  .divider {
    border-left: 1px solid $common-mid-grey;
    margin-left: 20px;
  }

</style>
